import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';

import { CAMADA } from '@/lib/camadas';
import { cn } from '@/lib/cn';

/**
 * <Popover> canônico — CENSO-CONTEUDO-FLUTUANTE (2026-08-05).
 *
 * Extraído do padrão que o Tooltip (jul/2026) e o Calendario (05/08) tiveram
 * que aprender cada um por si, depois do TERCEIRO componente flutuante clipado
 * por container com overflow: **conteúdo flutuante NASCE EM PORTAL**.
 *
 * O que ele garante, pra qualquer painel (listbox, menu, picker):
 *  - portal no `body` — nenhum `overflow: hidden/auto` de ancestral clipa;
 *  - `position: fixed` calculado do rect do gatilho;
 *  - FLIP vertical (embaixo → em cima) e, sem espaço nos dois, encosta no
 *    rodapé da viewport (lição do incidente de altura: dentro de modal não
 *    existe rolagem de página que alcance um painel fixed);
 *  - SHIFT horizontal clampado na viewport;
 *  - reposição em scroll (captura, pega scroll de contêiner interno), resize
 *    e mudança de tamanho do próprio painel (ResizeObserver);
 *  - Esc e clique fora fecham (as DUAS árvores contam como "dentro");
 *  - camada única da escada (`CAMADA.FLUTUANTE`) — nunca `z-*` solto.
 *
 * Headless de propósito: o dono controla `aberto` e o conteúdo; o Popover só
 * resolve o problema que já erramos três vezes — onde o painel aparece.
 */
export function Popover(props: {
  aberto: boolean;
  /** Ref do elemento que ancora o painel (input, botão…). */
  gatilhoRef: RefObject<HTMLElement | null>;
  /** Chamado no Esc e no clique fora (fora do gatilho E do painel). */
  onFechar: () => void;
  children: ReactNode;
  /** Classes do painel (borda/fundo/sombra ficam no caller quando ele quiser variar). */
  className?: string;
  /** Painel com a MESMA largura do gatilho (padrão de listbox/combobox). */
  larguraDoGatilho?: boolean;
  testId?: string;
}) {
  const { aberto, gatilhoRef, onFechar, larguraDoGatilho } = props;
  const painelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width?: number } | null>(null);
  const FOLGA = 8;

  const reposicionar = useCallback(() => {
    const gatilho = gatilhoRef.current;
    const painel = painelRef.current;
    if (!gatilho || !painel) return;
    const r = gatilho.getBoundingClientRect();
    const p = painel.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cabeEmbaixo = r.bottom + 4 + p.height <= vh - FOLGA;
    const cabeEmCima = r.top - 4 - p.height >= FOLGA;
    const top = cabeEmbaixo
      ? r.bottom + 4
      : cabeEmCima
        ? r.top - 4 - p.height
        : Math.max(FOLGA, vh - FOLGA - p.height);
    const left = Math.max(FOLGA, Math.min(r.left, vw - p.width - FOLGA));
    setPos({
      top: Math.max(FOLGA, top),
      left,
      ...(larguraDoGatilho ? { width: r.width } : {}),
    });
  }, [gatilhoRef, larguraDoGatilho]);

  useLayoutEffect(() => {
    if (!aberto) {
      setPos(null);
      return;
    }
    reposicionar();
  }, [aberto, reposicionar]);

  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A-7 (AUDITORIA-COMERCIAL): o Esc é CONSUMIDO aqui — em captura e com
      // stopImmediatePropagation — pra camada de fora (tela cheia, Dialog) não
      // fechar junto. Esc no dropdown fecha SÓ o dropdown; o vendedor com nota
      // meio escrita não perde a tela.
      e.stopImmediatePropagation();
      e.preventDefault();
      onFechar();
    };
    const aoClicar = (e: MouseEvent) => {
      const alvo = e.target as Node;
      const dentro = gatilhoRef.current?.contains(alvo) || painelRef.current?.contains(alvo);
      if (!dentro) onFechar();
    };
    // Captura: scroll de QUALQUER contêiner (zona de tela cheia, painel de
    // modal) move o gatilho — o painel acompanha.
    // Captura: roda ANTES dos listeners de bolha das telas cheias/Dialog.
    window.addEventListener('keydown', aoTeclar, true);
    document.addEventListener('mousedown', aoClicar);
    window.addEventListener('scroll', reposicionar, true);
    window.addEventListener('resize', reposicionar);
    const ro = new ResizeObserver(reposicionar);
    if (painelRef.current) ro.observe(painelRef.current);
    if (gatilhoRef.current) ro.observe(gatilhoRef.current);
    return () => {
      window.removeEventListener('keydown', aoTeclar, true);
      document.removeEventListener('mousedown', aoClicar);
      window.removeEventListener('scroll', reposicionar, true);
      window.removeEventListener('resize', reposicionar);
      ro.disconnect();
    };
  }, [aberto, onFechar, reposicionar, gatilhoRef]);

  if (!aberto || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={painelRef}
      data-testid={props.testId}
      style={{
        position: 'fixed',
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        ...(pos?.width != null ? { width: pos.width } : {}),
        visibility: pos ? 'visible' : 'hidden',
        zIndex: CAMADA.FLUTUANTE,
      }}
      className={cn('max-h-[min(60vh,calc(100vh-16px))] overflow-y-auto', props.className)}
    >
      {props.children}
    </div>,
    document.body,
  );
}
